// src/pages/sessions/components/LiveSession/managers/GoogleDocManager.jsx
import { useState, useCallback, useEffect } from 'react';
import { DataPacket_Kind } from 'livekit-client';
import { toast } from 'sonner';
import { X, FileText, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const encoder = new TextEncoder();

// Modal Component (included in the same file)
function GoogleDocModal({ isOpen, onClose, onSubmit }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setUrl('');
      setError('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!url.trim()) {
      setError(t('liveSession.googleDoc.modal.errors.emptyUrl'));
      return;
    }

    try {
      const parsedUrl = new URL(url);
      if (!parsedUrl.hostname.includes('docs.google.com')) {
        setError(t('liveSession.googleDoc.modal.errors.invalidUrl'));
        return;
      }
    } catch (e) {
      setError(t('liveSession.googleDoc.modal.errors.invalidFormat'));
      return;
    }

    onSubmit(url);
    setUrl('');
    setError('');
  };

  const openGoogleSignIn = () => {
    window.open('https://accounts.google.com/signin', '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-cyan-500" />
            <h2 className="text-xl font-semibold text-white">
              {t('liveSession.googleDoc.modal.title')}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Sign in reminder - always visible */}
          <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <p className="text-blue-200 text-sm">
                  {t('liveSession.googleDoc.modal.signInReminder')}
                </p>
              </div>
              <button
                onClick={openGoogleSignIn}
                className="flex items-center gap-2 px-3 py-1.5 bg-white text-gray-800 rounded hover:bg-gray-100 transition-colors text-xs font-medium whitespace-nowrap"
              >
                <img 
                  src="https://www.google.com/favicon.ico" 
                  alt="Google" 
                  className="w-3 h-3"
                />
                {t('liveSession.googleDoc.modal.signInButton')}
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-gray-800 rounded-lg p-4 space-y-3">
            <p className="text-white font-medium text-sm">
              {t('liveSession.googleDoc.modal.instructionsTitle')}
            </p>
            <div className="space-y-2 text-gray-300 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-cyan-500 mt-0.5">1.</span>
                <span>{t('liveSession.googleDoc.modal.instructions.signIn')}</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-cyan-500 mt-0.5">2.</span>
                <span>
                  {t('liveSession.googleDoc.modal.instructions.permissions')}
                </span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-cyan-500 mt-0.5">3.</span>
                <span>{t('liveSession.googleDoc.modal.instructions.individual')}</span>
              </div>
            </div>
          </div>

          {/* URL Input */}
          <div>
            <label className="block text-sm text-gray-300 mb-2">
              {t('liveSession.googleDoc.modal.urlLabel')}
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                setError('');
              }}
              placeholder={t('liveSession.googleDoc.modal.urlPlaceholder')}
              className="w-full px-4 py-2.5 bg-gray-800 text-white rounded-lg border border-gray-600 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 placeholder-gray-500"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit();
              }}
            />
            {error && (
              <p className="text-red-400 text-sm mt-2 flex items-center gap-1">
                <span className="inline-block w-1 h-1 bg-red-400 rounded-full" />
                {error}
              </p>
            )}
          </div>

          {/* Supported types */}
          <div className="flex items-center gap-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              {t('liveSession.googleDoc.modal.supportedTypes.docs')}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              {t('liveSession.googleDoc.modal.supportedTypes.sheets')}
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full" />
              {t('liveSession.googleDoc.modal.supportedTypes.slides')}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium"
          >
            {t('liveSession.googleDoc.modal.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            className="flex-1 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!url.trim()}
          >
            {t('liveSession.googleDoc.modal.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Main Google Doc Manager Hook
export function useGoogleDocManager({ roomRef, user, screenSharers, setLayoutMode }) {
  const { t } = useTranslation();
  const [embeddedDocument, setEmbeddedDocument] = useState(null);
  const [documentSharer, setDocumentSharer] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Process and clean Google Doc URL
  const processGoogleDocUrl = (inputUrl) => {
    const parsedUrl = new URL(inputUrl);
    
    // Clean up URL parameters
    parsedUrl.searchParams.delete('usp');
    parsedUrl.searchParams.delete('authuser');
    parsedUrl.searchParams.delete('resourcekey');
    parsedUrl.hash = '';
    
    let embedUrl = parsedUrl.toString();
    
    // Format based on document type
    if (embedUrl.includes('docs.google.com/document')) {
      const docId = embedUrl.match(/\/d\/([a-zA-Z0-9-_]+)/)?.[1];
      if (docId) {
        embedUrl = `https://docs.google.com/document/d/${docId}/edit`;
      }
    } else if (embedUrl.includes('docs.google.com/spreadsheets')) {
      const sheetId = embedUrl.match(/\/d\/([a-zA-Z0-9-_]+)/)?.[1];
      if (sheetId) {
        embedUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/edit`;
      }
    } else if (embedUrl.includes('docs.google.com/presentation')) {
      const slidesId = embedUrl.match(/\/d\/([a-zA-Z0-9-_]+)/)?.[1];
      if (slidesId) {
        embedUrl = `https://docs.google.com/presentation/d/${slidesId}/edit`;
      }
    }
    
    return embedUrl;
  };

  // Send document activity to room
  const sendDocumentActivity = useCallback(async (action) => {
    if (!roomRef.current) return;
    
    const message = {
      type: 'document_activity',
      action,
      user: user?.displayName || user?.email,
      timestamp: Date.now()
    };
    
    try {
      const encodedMessage = encoder.encode(JSON.stringify(message));
      await roomRef.current.localParticipant.publishData(
        encodedMessage, 
        DataPacket_Kind.RELIABLE
      );
    } catch (error) {
      console.error('Failed to send document activity:', error);
    }
  }, [roomRef, user]);

  // Handle modal submission
  const handleModalSubmit = useCallback(async (inputUrl) => {
    try {
      const embedUrl = processGoogleDocUrl(inputUrl);
      
      setEmbeddedDocument(embedUrl);
      setDocumentSharer('self');
      setLayoutMode('presentation');
      setShowModal(false);
      
      await sendDocumentActivity('started');
      
      toast.success(t('liveSession.googleDoc.toast.embedSuccess'));
      toast.info(t('liveSession.googleDoc.toast.embedInfo'), { 
        duration: 5000 
      });
    } catch (error) {
      console.error('Google Doc embed error:', error);
      toast.error(t('liveSession.googleDoc.toast.embedFailed'));
    }
  }, [sendDocumentActivity, setLayoutMode, t]);

  // Toggle document sharing
  const toggleDocumentShare = useCallback(async () => {
    if (embeddedDocument) {
      // Close document
      setEmbeddedDocument(null);
      setDocumentSharer(null);
      
      if (screenSharers.length === 0) {
        setLayoutMode('grid');
      }
      
      await sendDocumentActivity('stopped');
      toast.success(t('liveSession.googleDoc.toast.embedClosed'));
    } else {
      // Open modal to share document
      setShowModal(true);
    }
  }, [embeddedDocument, screenSharers, setLayoutMode, sendDocumentActivity, t]);

  // Render modal component
  const GoogleDocModalComponent = useCallback(() => (
    <GoogleDocModal
      isOpen={showModal}
      onClose={() => setShowModal(false)}
      onSubmit={handleModalSubmit}
    />
  ), [showModal, handleModalSubmit]);

  return {
    // State
    embeddedDocument,
    documentSharer,
    
    // Actions
    toggleDocumentShare,
    
    // Modal
    GoogleDocModalComponent,
    showModal,
    setShowModal,
  };
}